// Fixture data for the white-label demo page (#/profile-customization/demo).
//
// Deliberately separate from src/mocks, which is a dev-only harness switched on by
// VUE_APP_MOCK_MODE: this module backs a page real customers are shown, so it is
// maintained as presentation content. Both branded pages pull it in through a dynamic
// import so it stays off their own module graph. (Note this build currently inlines every
// async chunk into app.js, so that only pays off once route-level splitting really works.)
//
// Sizes follow the same contract the API does (see util/dataSize): `size` stays in the
// package's display unit while `availableBalance` drops to MB below 1 GB.

import {CustomerProfile, CustomerBalance, Country} from "@/interfaces/entities/CustomerProfile";
import {EsimProfile} from "@/interfaces/entities/EsimProfile";
import {GetESIMDataResponse} from "@/interfaces/httpResponses/GetESIMDataResponse";
import {PLACEHOLDER_LOGO_URL} from "@/demo/placeholderLogo";

export {PLACEHOLDER_LOGO_URL};

export type DemoScenario = "healthy" | "lowData" | "notInstalled" | "unlimited" | "expired";

export const DEMO_SCENARIOS: Array<DemoScenario> = ["healthy", "lowData", "notInstalled", "unlimited", "expired"];

const DAY_MS = 24 * 60 * 60 * 1000;
const DEMO_ICCID = "89012345678901234567";
const DEMO_DOMAIN_ID = "demo-domain-0001";
const DEMO_ACTIVATION_CODE = "LPA:1$rsp.example-esim.com$4F2A9C71B8E34D06A5C1907E23B4D8F0";

const iso = (offsetMs: number) => new Date(Date.now() + offsetMs).toISOString();

const country = (code: string, name: string): Country => ({
    code,
    name,
    // Same endpoint the real API points at, so flags render exactly as in production.
    imageUrl: `/esim/countries/${code.toLowerCase()}/flag`
});

const EUROPE: Array<Country> = [
    country("AT", "Austria"), country("BE", "Belgium"), country("HR", "Croatia"),
    country("CZ", "Czech Republic"), country("DK", "Denmark"), country("FI", "Finland"),
    country("FR", "France"), country("DE", "Germany"), country("GR", "Greece"),
    country("HU", "Hungary"), country("IE", "Ireland"), country("IT", "Italy"),
    country("NL", "Netherlands"), country("NO", "Norway"), country("PL", "Poland"),
    country("PT", "Portugal"), country("RO", "Romania"), country("SK", "Slovakia"),
    country("SI", "Slovenia"), country("ES", "Spain"), country("SE", "Sweden"),
    country("CH", "Switzerland")
];

interface ScenarioShape {
    packageName: string;
    sizeValue: number;
    sizeUnit: string;
    remainingValue: number;
    remainingUnit: string;
    validityDays: number;
    daysSinceActivation: number | null; // null = purchased but never installed
    esimState: string;
}

const SHAPES: Record<DemoScenario, ScenarioShape> = {
    // Comfortable mid-trip plan - the state most end users will see.
    healthy: {
        packageName: "Europe 10 GB",
        sizeValue: 10, sizeUnit: "GB",
        remainingValue: 6.4, remainingUnit: "GB",
        validityDays: 30, daysSinceActivation: 9,
        esimState: "Installed"
    },
    // Under the 20% low-data threshold, so the warning banner and amber ring show.
    // Note the MB unit against a GB package - the cross-unit case the API really sends.
    lowData: {
        packageName: "Europe 10 GB",
        sizeValue: 10, sizeUnit: "GB",
        remainingValue: 819.2, remainingUnit: "MB",
        validityDays: 30, daysSinceActivation: 11,
        esimState: "Installed"
    },
    // Purchased, eSIM released but not yet on a device - shows the install prompt CTA.
    notInstalled: {
        packageName: "Europe 5 GB",
        sizeValue: 5, sizeUnit: "GB",
        remainingValue: 5, remainingUnit: "GB",
        validityDays: 14, daysSinceActivation: null,
        esimState: "Released"
    },
    // Validity window already gone. daysSinceActivation past validityDays puts expiresAt in the
    // past, which is what the expired card keys off - data left over is deliberate, since expiry
    // trumps a remaining balance.
    expired: {
        packageName: "Asia Pacific 8 GB",
        sizeValue: 8, sizeUnit: "GB",
        remainingValue: 2.1, remainingUnit: "GB",
        validityDays: 30, daysSinceActivation: 45,
        esimState: "Installed"
    },
    // Daily full-speed quota plus throttling afterwards.
    unlimited: {
        packageName: "Europe Unlimited",
        sizeValue: 2, sizeUnit: "GB",
        remainingValue: 0, remainingUnit: "GB",
        validityDays: 15, daysSinceActivation: 4,
        esimState: "Installed"
    }
};

function timestamps(shape: ScenarioShape) {
    const activatedAt = shape.daysSinceActivation === null ? null : iso(-shape.daysSinceActivation * DAY_MS);
    const elapsed = (shape.daysSinceActivation ?? 0) * DAY_MS;
    return {
        activatedAt,
        expiresAt: iso(shape.validityDays * DAY_MS - elapsed),
        salesDate: iso(-((shape.daysSinceActivation ?? 0) + 1) * DAY_MS)
    };
}

function extendedPolicy(scenario: DemoScenario, shape: ScenarioShape) {
    if (scenario !== "unlimited") return null;
    return {
        type: "unlimited",
        resetType: "timeInterval",
        interval: "24h",
        fixedTime: "00:00",
        throttleSpeed: "512kb/s",
        totalHighSpeedVolume: {sizeValue: shape.sizeValue, sizeUnit: shape.sizeUnit},
        remainingHighSpeedVolume: {sizeValue: 1.2, sizeUnit: shape.sizeUnit},
        nextResetTime: iso(8 * 60 * 60 * 1000)
    };
}

function balance(scenario: DemoScenario) {
    const shape = SHAPES[scenario];
    const {activatedAt, expiresAt} = timestamps(shape);
    return {
        name: shape.packageName,
        activatedAt,
        expiresAt,
        activationMode: "FIRST_USE",
        size: {sizeValue: shape.sizeValue, sizeUnit: shape.sizeUnit},
        availableBalance: {sizeValue: shape.remainingValue, sizeUnit: shape.remainingUnit},
        validitySize: shape.validityDays,
        validityUnit: "days",
        countrySet: "eur",
        extendedDataPolicy: extendedPolicy(scenario, shape)
    };
}

function esimProfile(scenario: DemoScenario): EsimProfile {
    const shape = SHAPES[scenario];
    const {activatedAt} = timestamps(shape);
    return {
        uid: `demo-esim-${scenario}`,
        active: true,
        state: shape.esimState,
        iccid: DEMO_ICCID,
        imsi: "",
        customerProfileDomainId: DEMO_DOMAIN_ID,
        activatedAt,
        lastSeen: activatedAt === null ? null : iso(-25 * 60 * 1000),
        // Kept relative so the demo's "install" CTA stays inside the demo page.
        installationUrl: "",
        activationCode: DEMO_ACTIVATION_CODE,
        appleUniversalLink: `https://esimsetup.apple.com/esim_qrcode_provisioning?carddata=${encodeURIComponent(DEMO_ACTIVATION_CODE)}`,
        androidUniversalLink: `https://esim.example.com/android?code=${encodeURIComponent(DEMO_ACTIVATION_CODE)}`
    };
}

export function demoCustomerProfile(scenario: DemoScenario): CustomerProfile {
    const shape = SHAPES[scenario];
    const {salesDate} = timestamps(shape);
    const customerBalance: CustomerBalance = {
        price: {priceValue: 24.9, currencyCode: "EUR"},
        salesDate,
        customerProfileDomainId: DEMO_DOMAIN_ID,
        includedCountries: EUROPE,
        balance: balance(scenario) as CustomerBalance["balance"]
    };
    return {
        balances: [customerBalance],
        relatedEsims: [esimProfile(scenario)],
        totalAvailableBalance: {sizeValue: shape.remainingValue, sizeUnit: shape.remainingUnit}
        // resellerInformation is intentionally absent - the demo page supplies branding itself.
    };
}

export function demoEsimInstallation(scenario: DemoScenario): GetESIMDataResponse["data"] {
    const shape = SHAPES[scenario];
    return {
        esimProfile: esimProfile(scenario),
        balances: [balance(scenario) as GetESIMDataResponse["data"]["balances"][0]],
        totalAvailableBalance: {sizeValue: shape.remainingValue, sizeUnit: shape.remainingUnit},
        customerProfileUrl: ""
    };
}
